import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import {
  LiveKitRoom,
  StartAudio,
  useConnectionState,
  useLocalParticipant,
  useTracks,
  useTranscriptions,
  useVoiceAssistant,
} from "@livekit/components-react";
import { ConnectionState, Track } from "livekit-client";
import {
  validateDynamicVoiceCode,
  fetchTenantBranding,
} from "../../Redux/ActionCreators/dynamicVoiceCallAction";
import DynamicVoiceInterviewThankYou from "./DynamicVoiceInterviewThankYou";
import "./DynamicVoiceInterviewMain.scss";

const LEAVE_LOCK_SECONDS = 300;

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// The backend puts the OTP in the invite link as ?code=<base64>. Accept both
// standard and URL-safe base64 (and missing "=" padding) since it's coming
// from a URL query string rather than a strict base64 API.
const decodeBase64Code = (value) => {
  if (!value) return "";
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch {
    return "";
  }
};

// Converts a "#rrggbb" tenant brand color into an "r, g, b" triplet so it can
// feed the alpha-blended box-shadow/gradient effects (which need raw rgb
// components, not a hex string) via a second CSS custom property.
const hexToRgb = (hex) => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || "").trim());
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
};

// Facebook/Instagram/Messenger's in-app browser is a stripped-down WebView with
// no address bar or site-info/lock icon, so "click the lock icon to change
// permissions" has nothing to point at there. The only real recovery path is
// opening the link in the system browser (or the host app's own permission
// screen), so we detect it and swap the copy instead of showing dead advice.
const isInAppBrowser = () =>
  /FBAN|FBAV|FB_IAB|Instagram|Line\//i.test(navigator.userAgent || "");

// Fullscreen helpers with the webkit-prefixed fallbacks older Safari/iPadOS
// still need. iPhone Safari doesn't support element fullscreen at all, so
// every call is a silent no-op there rather than an error.
const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const enterFullscreen = () => {
  if (getFullscreenElement()) return;
  const el = document.documentElement;
  const request = el.requestFullscreen || el.webkitRequestFullscreen;
  if (!request) return;
  try {
    const result = request.call(el);
    if (result && typeof result.catch === "function") result.catch(() => {});
  } catch {
    // Not allowed without a user gesture, or unsupported -- ignore.
  }
};

const exitFullscreen = () => {
  if (!getFullscreenElement()) return;
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  if (!exit) return;
  try {
    const result = exit.call(document);
    if (result && typeof result.catch === "function") result.catch(() => {});
  } catch {
    // ignore
  }
};

// Static ring of thin, gradient-stroked ellipses drawn around the avatar to
// form the spirograph-style "thinking" orbit; each is just rotated a bit
// further than the last so the woven-sphere look falls out of overlap.
const ORBIT_ELLIPSE_COUNT = 16;
const ORBIT_ELLIPSES = Array.from({ length: ORBIT_ELLIPSE_COUNT }, (_, i) => ({
  angle: i * (180 / ORBIT_ELLIPSE_COUNT),
  opacity: 0.22 + (i % 4) * 0.14,
}));

// ─── Remote audio playback (must live inside <LiveKitRoom>) ──────────────────

// iOS Safari routes call audio to the earpiece -- not the speaker -- whenever
// a microphone is active and remote audio plays through an <audio> element;
// WebKit treats that combination as a phone call. Attaching the exact same
// remote MediaStreamTrack to a <video> element instead makes WebKit pick the
// loudspeaker route. iOS Safari has no direct "force speaker output" API, so
// this is the standard workaround used by WebRTC apps to get speaker audio.
const RemoteAudioTrack = ({ trackRef }) => {
  const videoRef = useRef(null);
  const track = trackRef.publication?.track;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !track) return undefined;
    track.attach(el);
    el.play().catch(() => {});
    return () => track.detach(el);
  }, [track]);

  return <video ref={videoRef} autoPlay playsInline muted={false} className="dv-remote-audio-el" />;
};

const RemoteAudioRenderer = () => {
  const audioTrackRefs = useTracks(
    [Track.Source.Microphone, Track.Source.ScreenShareAudio, Track.Source.Unknown],
    { updateOnlyOn: [], onlySubscribed: true }
  ).filter((ref) => !ref.participant.isLocal && ref.publication.kind === Track.Kind.Audio);

  return (
    <>
      {audioTrackRefs.map((ref) => (
        <RemoteAudioTrack key={ref.publication.trackSid} trackRef={ref} />
      ))}
    </>
  );
};

// ─── Room content (must live inside <LiveKitRoom>) ───────────────────────────

const DynamicVoiceRoomContent = ({ connectionDetails, onLeave, micTrack, logoSrc }) => {
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const connectionState = useConnectionState();

  // The prejoin screen already captured the mic (to prove access) on the same
  // physical device the candidate approved, so <LiveKitRoom> is mounted with
  // audio={false} and we publish that already-live track ourselves instead of
  // letting the SDK call getUserMedia a second time. On iOS, stopping the
  // preview stream and immediately re-requesting the mic -- especially with a
  // Bluetooth headset connected -- can race the OS's audio-route negotiation,
  // so the second getUserMedia call resolves "granted" but captures silence.
  useEffect(() => {
    if (!micTrack || micTrack.readyState === "ended") return undefined;
    if (connectionState !== ConnectionState.Connected) return undefined;
    const alreadyPublished = Array.from(localParticipant.audioTrackPublications.values()).some(
      (pub) => pub.track?.mediaStreamTrack === micTrack
    );
    if (alreadyPublished) return undefined;
    localParticipant
      .publishTrack(micTrack, { source: Track.Source.Microphone, name: "microphone" })
      .catch((err) => console.error("Failed to publish microphone track", err));
    return undefined;
  }, [connectionState, localParticipant, micTrack]);

  // useVoiceAssistant looks up the LiveKit Agents participant by its `kind`
  // (ParticipantKind.AGENT), not by identity naming convention, so it works
  // regardless of what identity string the backend assigns to the agent.
  const { agent, state: agentState } = useVoiceAssistant();
  const agentJoined = !!agent;
  const agentSpeaking = agentState === "speaking";
  // The agent reports "thinking" for the gap between the candidate finishing
  // their turn and the assistant's speech actually starting.
  const agentThinking = agentState === "thinking";

  // Leave stays locked for the first 5 minutes after the assistant joins the room.
  const [leaveCountdown, setLeaveCountdown] = useState(LEAVE_LOCK_SECONDS);
  useEffect(() => {
    if (!agentJoined || leaveCountdown <= 0) return undefined;
    const timer = setTimeout(() => setLeaveCountdown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [agentJoined, leaveCountdown]);
  const leaveLocked = leaveCountdown > 0;

  // The agent publishes live speech-to-text via LiveKit's text-stream
  // transcription topic, not the legacy RoomEvent.TranscriptionReceived
  // event, so we read it through the dedicated hook.
  const rawTranscriptions = useTranscriptions();
  const transcriptEndRef = useRef(null);

  const transcript = useMemo(
    () =>
      rawTranscriptions
        .filter((item) => item.text && item.text.trim())
        .map((item) => ({
          id: item.streamInfo.id,
          role: item.participantInfo.identity === localParticipant?.identity ? "candidate" : "assistant",
          text: item.text.trim(),
        })),
    [rawTranscriptions, localParticipant]
  );

  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript]);

  const titleText =
    connectionDetails?.voice_call?.job?.title ||
    connectionDetails?.voice_call?.title ||
    "Voice Screening";

  return (
    <div className="dv-room">
      {/* Header */}
      <div className="dv-room-header">
        <img src={logoSrc} alt="logo" />
        <h1>{titleText}</h1>
      </div>

      {/* Browsers often block autoplay until the user interacts with the page;
          this button stays hidden once audio is already playing. */}
      <StartAudio label="Click to enable audio" className="dv-start-audio-banner" />

      {/* Body */}
      <div className="dv-room-body">
        {!agentJoined ? (
          <div className="dv-waiting">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Waiting for the assistant to join…</p>
          </div>
        ) : (
          <>
            <div className="dv-main-area">
              <div className="dv-avatar-area">
                <div className="dv-avatar-wrap">
                  <div
                    className={`dv-thinking-orbit${agentThinking ? " active" : ""}`}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 200 200" className="dv-orbit-svg">
                      <defs>
                        <linearGradient id="dvOrbitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#8b5cf6" />
                          <stop offset="50%" stopColor="#6366f1" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                      {ORBIT_ELLIPSES.map(({ angle, opacity }) => (
                        <ellipse
                          key={angle}
                          cx="100"
                          cy="100"
                          rx="92"
                          ry="38"
                          transform={`rotate(${angle} 100 100)`}
                          fill="none"
                          stroke="url(#dvOrbitGradient)"
                          strokeWidth="0.6"
                          opacity={opacity}
                        />
                      ))}
                    </svg>
                  </div>
                  <div
                    className={`dv-avatar${agentSpeaking ? " speaking" : ""}${
                      agentThinking ? " thinking" : ""
                    }`}
                  >
                    <span>AI</span>
                  </div>
                </div>
                <div className="dv-avatar-label">
                  {agentThinking ? "Assistant is thinking…" : "Assistant is on the call"}
                </div>
              </div>
            </div>

            <div className="dv-transcript-panel">
              <div className="dv-transcript-header">
                <span>Transcript</span>
              </div>
              <div className="dv-transcript">
                {transcript.length === 0 ? (
                  <p className="dv-transcript-empty">The conversation transcript will appear here.</p>
                ) : (
                  <>
                    {transcript.map((msg) => (
                      <div key={msg.id} className={`dv-msg dv-msg-${msg.role}`}>
                        <span className="who">{msg.role === "candidate" ? "You" : "Assistant"}</span>
                        <span>{msg.text}</span>
                      </div>
                    ))}
                    <div ref={transcriptEndRef} />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Controls */}
      <div className="dv-controls-bar">
        <RemoteAudioRenderer />

        <button
          className={`ctrl-btn ${!isMicrophoneEnabled ? "ctrl-btn-off" : ""}`}
          onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
          title={isMicrophoneEnabled ? "Mute microphone" : "Unmute microphone"}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            {isMicrophoneEnabled ? (
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z" />
            ) : (
              <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
            )}
          </svg>
          <span>{isMicrophoneEnabled ? "Mute" : "Unmute"}</span>
        </button>

        <button
          className="ctrl-btn leave-ctrl-btn"
          onClick={onLeave}
          disabled={leaveLocked}
          title={leaveLocked ? `Available in ${formatCountdown(leaveCountdown)}` : "Leave"}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
          <span style={{color:'white'}}>{leaveLocked ? formatCountdown(leaveCountdown) : "Leave"}</span>
        </button>
      </div>

      {/* Mobile-only call controls (WhatsApp-style circular buttons) */}
      <div className="dv-mobile-call-bar">
        <button
          className={`dv-mobile-ctrl ${!isMicrophoneEnabled ? "off" : ""}`}
          onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
        >
          <span className="dv-mobile-ctrl-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              {isMicrophoneEnabled ? (
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z" />
              ) : (
                <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
              )}
            </svg>
          </span>
          <span className="dv-mobile-ctrl-label">{isMicrophoneEnabled ? "Mute" : "Unmute"}</span>
        </button>

        <button className="dv-mobile-ctrl" disabled title="Share">
          <span className="dv-mobile-ctrl-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
            </svg>
          </span>
          <span className="dv-mobile-ctrl-label">Share</span>
        </button>

        <button
          className="dv-mobile-ctrl dv-mobile-ctrl-end"
          onClick={onLeave}
          disabled={leaveLocked}
          title={leaveLocked ? `Available in ${formatCountdown(leaveCountdown)}` : "End"}
        >
          <span className="dv-mobile-ctrl-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 01-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
            </svg>
          </span>
          <span className="dv-mobile-ctrl-label">
            {leaveLocked ? formatCountdown(leaveCountdown) : "End"}
          </span>
        </button>
      </div>
    </div>
  );
};

// ─── Pre-join screen ──────────────────────────────────────────────────────────

const PrejoinScreen = ({
  sessionId,
  onJoined,
  dynamicVoiceValidating,
  snackbar,
  setSnackbar,
  micGranted,
  micDenied,
  micUnsupported,
  onGrantMic,
  initialCode,
  logoSrc,
}) => {
  const dispatch = useDispatch();

  const [speakerGranted, setSpeakerGranted] = useState(false);
  const [verificationCode, setVerificationCode] = useState(initialCode || "");
  // Computed once per mount, not per render -- the UA doesn't change mid-session.
  const inAppBrowser = useMemo(() => isInAppBrowser(), []);
  // Re-shown any time a fresh denial comes in (e.g. the candidate dismissed
  // it, then clicked "Allow" again and got blocked a second time).
  const [showMicOverlay, setShowMicOverlay] = useState(false);
  useEffect(() => {
    if (micDenied) setShowMicOverlay(true);
  }, [micDenied]);

  const canVerify = micGranted && speakerGranted;
  // The invite link carries the OTP as ?code=, already decoded into
  // initialCode -- when it's there, the candidate never needs to type it.
  const hasPrefilledCode = Boolean(initialCode);

  // Joining is gated behind a "do you have 15-30 minutes?" confirmation so a
  // candidate doesn't start the interview (and burn their invite) by accident.
  const [showStartConfirm, setShowStartConfirm] = useState(false);

  const handleVerifyClick = () => {
    if (!verificationCode.trim()) {
      setSnackbar({ open: true, message: "Please enter your verification code.", severity: "error" });
      return;
    }
    setShowStartConfirm(true);
  };

  const handleVerify = async () => {
    setShowStartConfirm(false);
    // Must run synchronously inside the "Start" click -- browsers only allow
    // fullscreen during a user gesture, which is lost after the await below.
    enterFullscreen();
    const result = await dispatch(validateDynamicVoiceCode(sessionId, verificationCode));
    if (result.success) {
      onJoined();
    } else {
      exitFullscreen();
      setSnackbar({
        open: true,
        message: result.message || "Invalid verification code",
        severity: "error",
      });
    }
  };

  return (
    <div className="dv-prejoin">
      {micDenied && showMicOverlay && (
        <div className="dv-mic-blocked-overlay" role="dialog" aria-modal="true">
          {/* In-app browsers (Facebook/Instagram/Messenger) have no address bar
              or lock icon for the arrow to point at, so skip it there. */}
          {!inAppBrowser && (
            <svg
              className="dv-mic-blocked-arrow"
              viewBox="0 0 140 170"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M130 165 C 55 150, 15 95, 18 12"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M4 26 L18 6 L33 22"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          )}

          <div className="dv-mic-blocked-card">
            <h3>Allow microphone</h3>
            {inAppBrowser ? (
              <p>
                This page is open inside Facebook/Instagram's in-app browser, which has no
                address bar or site-info icon to change permissions from. Tap the{" "}
                <strong>⋯</strong> menu in the top corner and choose{" "}
                <strong>Open in Browser</strong> (Chrome/Safari), then reopen this link there
                and allow the microphone when asked.
              </p>
            ) : (
              <>
                <p>
                  You've blocked microphone access for this site, so the browser won't ask again
                  automatically. Click the lock/site-info icon at the address bar above, turn
                  "Microphone" on, then reload this page.
                </p>
                <p className="dv-mic-blocked-mobile">
                  On mobile, if you don't see that option in the address bar, open your device's
                  Settings, find your browser app under Apps/Permissions, and allow Microphone there
                  instead.
                </p>
              </>
            )}
            <button
              className="dv-mic-blocked-btn"
              onClick={() => setShowMicOverlay(false)}
            >
              OK, got it
            </button>
          </div>
        </div>
      )}

      <div className="dv-prejoin-card">
        <img src={logoSrc} alt="logo" width={160} height={80} className="dv-prejoin-logo" />
        <h2>Join Voice Screening</h2>
        <p className="dv-prejoin-subtitle">Enter your verification code to join the audio screening.</p>

        <div className="dv-perm-status-list">
          <div className={`dv-perm-status-item ${micGranted ? "ok" : "warn"}`}>
            <span>{micGranted ? "✓" : "!"}</span> Microphone {micGranted ? "allowed" : "not allowed"}
            {!micGranted && (
              <button className="dv-grant-btn" onClick={onGrantMic}>
                Allow
              </button>
            )}
          </div>
          {micDenied && (
            <div className="dv-perm-denied-help">
              {inAppBrowser ? (
                <p>
                  This link opened inside Facebook/Instagram's in-app browser, which doesn't
                  expose a way to change site permissions. Tap the <strong>⋯</strong> menu and
                  choose <strong>Open in Browser</strong>, then reopen this link in Chrome or
                  Safari and allow the microphone there.
                </p>
              ) : (
                <>
                  <p>
                    You've blocked microphone access for this site, so the browser won't ask again
                    automatically. To enable it:
                  </p>
                  <ol>
                    <li>Click the lock/site-info icon in your browser's address bar (left of the URL).</li>
                    <li>Find "Microphone" under site permissions and set it to "Allow".</li>
                    <li>Reload this page and click "Allow" again.</li>
                  </ol>
                  <p>
                    On mobile, check your device's browser app permissions in system Settings if you
                    don't see this option in the address bar.
                  </p>
                </>
              )}
              {!showMicOverlay && (
                <button className="dv-perm-denied-link" onClick={() => setShowMicOverlay(true)}>
                  Show me how to allow it
                </button>
              )}
            </div>
          )}
          {micUnsupported && !micDenied && (
            <div className="dv-perm-denied-help">
              <p>
                We couldn't get microphone access in this browser
                {inAppBrowser ? " -- Facebook/Instagram's in-app browser has limited microphone support" : ""}.
                Please open this link in Chrome or Safari and try again.
              </p>
            </div>
          )}
          <div className={`dv-perm-status-item ${speakerGranted ? "ok" : "warn"}`}>
            <span>{speakerGranted ? "✓" : "!"}</span> Speaker {speakerGranted ? "allowed" : "not allowed"}
            {!speakerGranted && (
              <button className="dv-grant-btn" onClick={() => setSpeakerGranted(true)}>
                Allow
              </button>
            )}
          </div>
        </div>

        {!hasPrefilledCode && (
          <input
            className="dv-otp-input"
            type="text"
            placeholder="Enter verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canVerify && !dynamicVoiceValidating && handleVerifyClick()}
          />
        )}

        <button
          className="dv-verify-btn"
          onClick={handleVerifyClick}
          disabled={!canVerify || dynamicVoiceValidating}
        >
          {dynamicVoiceValidating ? "Verifying…" : "Verify & Join"}
        </button>

        {!canVerify && (
          <p className="dv-verify-hint">Allow microphone and speaker to enable this button.</p>
        )}
      </div>

      {showStartConfirm && (
        <div className="dv-start-confirm-overlay" role="dialog" aria-modal="true">
          <div className="dv-start-confirm-card">
            <h3>Before you begin</h3>
            <p>
              This interview takes <strong>10 to 20 minutes</strong> and can't be paused once started. Please begin only when you're in a quiet place with a stable internet connection.
            </p>
            <div className="dv-start-confirm-actions">
              <button
                className="dv-start-confirm-back"
                onClick={() => setShowStartConfirm(false)}
              >
                Back
              </button>
              <button className="dv-start-confirm-start" onClick={handleVerify}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main exported component ─────────────────────────────────────────────────

const DynamicVoiceInterviewMain = () => {
  const { token: sessionId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { dynamicVoiceValidating, dynamicVoiceConnectionDetails, tenantBranding } = useSelector(
    (state) => state.dynamicVoiceCall
  );

  const initialCode = useMemo(
    () => decodeBase64Code(new URLSearchParams(location.search).get("code")),
    [location.search]
  );

  // The branding token ("t") is a separate query param from both the :token
  // route param (the voice-call session id) and the "code" OTP param.
  const brandToken = useMemo(
    () => new URLSearchParams(location.search).get("t"),
    [location.search]
  );

  const [brandingLoading, setBrandingLoading] = useState(false);
  useEffect(() => {
    if (!brandToken) return;
    setBrandingLoading(true);
    dispatch(fetchTenantBranding(brandToken)).finally(() => setBrandingLoading(false));
  }, [brandToken, dispatch]);

  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);
  const logoSrc = isWhiteLabel && tenantBranding?.logo
    ? tenantBranding.logo
    : "/images/gappeoai-logo.png";

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty("--dv-accent-color", tenantBranding.colour);
      const rgb = hexToRgb(tenantBranding.colour);
      if (rgb) document.documentElement.style.setProperty("--dv-accent-rgb", rgb);
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  const [joined, setJoined] = useState(false);
  const [left, setLeft] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  // Held here (not inside PrejoinScreen) so the granted stream survives the
  // prejoin -> room transition instead of being torn down when PrejoinScreen
  // unmounts. See the publish effect in DynamicVoiceRoomContent for why we
  // reuse this exact track rather than letting LiveKitRoom re-acquire the mic.
  const micStreamRef = useRef(null);
  const [micGranted, setMicGranted] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  // Set for any failure that ISN'T a clear permission denial (missing
  // mediaDevices API, a getUserMedia call that never settles, no mic
  // hardware, etc.) -- common in in-app WebViews -- so the UI always shows
  // *something* persistent instead of silently doing nothing.
  const [micUnsupported, setMicUnsupported] = useState(false);

  const handleGrantMic = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      // Some in-app WebView builds don't expose getUserMedia at all -- there's
      // no prompt to retry, so surface this instead of throwing later.
      setMicUnsupported(true);
      setSnackbar({
        open: true,
        message: "This browser doesn't support microphone access. Please open this link in Chrome or Safari.",
        severity: "error",
      });
      return;
    }
    try {
      // Certain in-app WebViews (notably Facebook's) can leave the mic prompt
      // hanging indefinitely instead of resolving or rejecting it, which would
      // otherwise leave the Allow button appearing to do nothing.
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new DOMException("Timed out waiting for microphone permission", "TimeoutError")),
            8000
          )
        ),
      ]);
      micStreamRef.current = stream;
      setMicGranted(true);
      setMicDenied(false);
      setMicUnsupported(false);
    } catch (err) {
      // NotAllowedError/PermissionDeniedError means the user (or a prior
      // block) denied the prompt, so a repeat getUserMedia call won't
      // re-prompt — the candidate has to flip it in browser site settings.
      const isPermissionDenied =
        err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError");
      setMicDenied(isPermissionDenied);
      setMicUnsupported(!isPermissionDenied);
      setSnackbar({
        open: true,
        message: isPermissionDenied
          ? "Microphone access denied. Please allow it from your browser settings."
          : "Microphone access failed. Please allow mic access and try again.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    return () => {
      if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const handleLeave = () => setLeft(true);

  // Once the candidate is in the interview, ask the browser to confirm before
  // the tab is closed/reloaded so an accidental click doesn't end the call.
  // Browsers show their own generic "Leave site?" dialog and ignore custom text.
  useEffect(() => {
    if (!joined || left) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [joined, left]);

  // Keep the interview in fullscreen. Browsers won't let us block Esc or
  // re-enter fullscreen without a user gesture, so when the candidate exits
  // we warn them and restore fullscreen on their next click anywhere.
  useEffect(() => {
    if (!joined || left) return undefined;

    const handleFullscreenChange = () => {
      if (getFullscreenElement()) return;
      setSnackbar({
        open: true,
        message: "Please stay in full screen during the interview. Click anywhere to return to full screen.",
        severity: "warning",
      });
    };

    const handleClick = () => {
      if (!getFullscreenElement()) enterFullscreen();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("click", handleClick, true);
    };
  }, [joined, left]);

  // Drop out of fullscreen once the interview ends so the thank-you screen
  // shows in the normal browser window.
  useEffect(() => {
    if (left) exitFullscreen();
  }, [left]);

  useEffect(() => () => exitFullscreen(), []);

  if (brandingLoading) {
    return (
      <div className="dv-loading">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (left) {
    return <DynamicVoiceInterviewThankYou logoSrc={logoSrc} />;
  }

  return (
    <div className="dv-wrapper">
      {!joined ? (
        <PrejoinScreen
          sessionId={sessionId}
          onJoined={() => setJoined(true)}
          dynamicVoiceValidating={dynamicVoiceValidating}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
          micGranted={micGranted}
          micDenied={micDenied}
          micUnsupported={micUnsupported}
          onGrantMic={handleGrantMic}
          initialCode={initialCode}
          logoSrc={logoSrc}
        />
      ) : dynamicVoiceConnectionDetails ? (
        <LiveKitRoom
          serverUrl={dynamicVoiceConnectionDetails.live_kit.server_url}
          token={dynamicVoiceConnectionDetails.live_kit.participant_token}
          connect={true}
          video={false}
          audio={false}
          onDisconnected={handleLeave}
        >
          <DynamicVoiceRoomContent
            connectionDetails={dynamicVoiceConnectionDetails}
            onLeave={handleLeave}
            micTrack={micStreamRef.current?.getAudioTracks()[0]}
            logoSrc={logoSrc}
          />
        </LiveKitRoom>
      ) : (
        <div className="dv-loading">Loading session…</div>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DynamicVoiceInterviewMain;
